import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { FilterState, ExchangeConfig } from "@/types/stock.types";
import { NotificationPreferences } from "@/types/notification.types";
import { preferencesApi } from "@/services/api/endpoints/preferences.api";

// Dashboard Settings Interface
export interface DashboardSettings {
  autoRefresh: boolean;
  refreshInterval: string; // Keep as string for Select compatibility or number? Usage in component uses string. Keeping string to minimize component refactor.
  compactView: boolean;
  showPercentage: boolean;
  defaultView: "table" | "grid" | "chart";
}

export interface PreferencesState {
  filters: FilterState;
  theme: "light" | "dark" | "system";
  soundEnabled: boolean;
  dashboardSettings: DashboardSettings;
  notificationPreferences: NotificationPreferences | null;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: PreferencesState = {
  filters: {
    price: "any",
    changePercent: "any",
    exchanges: {
      all: true,
      nyse: true,
      otc: true,
      nasdaq: true,
      amex: true,
    },
    currentVolume: "any",
    relativeVolume: "any",
    float: "any",
    watchlistedSymbols: [],
  },
  theme: "system",
  soundEnabled: true,
  dashboardSettings: {
    autoRefresh: true,
    refreshInterval: "20",
    compactView: false,
    showPercentage: true,
    defaultView: "table",
  },
  notificationPreferences: null,
  loading: false,
  error: null,
};

// ... thunks omitted for brevity (unchanged) ...
export const fetchNotificationPreferences = createAsyncThunk(
  "preferences/fetchNotificationPrefs",
  async (_, { rejectWithValue }) => {
    try {
      const prefs = await preferencesApi.getNotificationPreferences();
      return prefs;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to fetch preferences");
    }
  },
);

export const updateNotificationPreferences = createAsyncThunk(
  "preferences/updateNotificationPrefs",
  async (prefs: Partial<NotificationPreferences>, { rejectWithValue }) => {
    try {
      const updated = await preferencesApi.updateNotificationPreferences(prefs);
      return updated;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to update preferences");
    }
  },
);

export const verifyPhone = createAsyncThunk(
  "preferences/verifyPhone",
  async (phoneNumber: string, { rejectWithValue }) => {
    try {
      await preferencesApi.verifyPhone(phoneNumber);
      return phoneNumber;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to verify phone");
    }
  },
);

// Slice
const preferencesSlice = createSlice({
  name: "preferences",
  initialState,
  reducers: {
    updateFilter: (
      state,
      action: PayloadAction<{ key: keyof FilterState; value: unknown }>,
    ) => {
      const { key, value } = action.payload;
      (state.filters[key] as unknown) = value;
    },
    updateFilters: (state, action: PayloadAction<Partial<FilterState>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    updateExchanges: (
      state,
      action: PayloadAction<Partial<ExchangeConfig>>,
    ) => {
      state.filters.exchanges = {
        ...state.filters.exchanges,
        ...action.payload,
      };
    },
    toggleExchange: (state, action: PayloadAction<keyof ExchangeConfig>) => {
      const exchange = action.payload;
      if (exchange === "all") {
        const newValue = !state.filters.exchanges.all;
        state.filters.exchanges = {
          all: newValue,
          nyse: newValue,
          otc: newValue,
          nasdaq: newValue,
          amex: newValue,
        };
      } else {
        state.filters.exchanges[exchange] = !state.filters.exchanges[exchange];
        // Update 'all' checkbox state
        const allSelected =
          state.filters.exchanges.nyse &&
          state.filters.exchanges.otc &&
          state.filters.exchanges.nasdaq &&
          state.filters.exchanges.amex;
        state.filters.exchanges.all = allSelected;
      }
    },
    addWatchlistedSymbol: (state, action: PayloadAction<string>) => {
      if (!state.filters.watchlistedSymbols.includes(action.payload)) {
        state.filters.watchlistedSymbols.push(action.payload);
      }
    },
    removeWatchlistedSymbol: (state, action: PayloadAction<string>) => {
      state.filters.watchlistedSymbols =
        state.filters.watchlistedSymbols.filter(
          (symbol) => symbol !== action.payload,
        );
    },
    setTheme: (state, action: PayloadAction<"light" | "dark" | "system">) => {
      state.theme = action.payload;
    },
    toggleSound: (state) => {
      state.soundEnabled = !state.soundEnabled;
    },
    updateDashboardSettings: (
      state,
      action: PayloadAction<Partial<DashboardSettings>>,
    ) => {
      state.dashboardSettings = {
        ...state.dashboardSettings,
        ...action.payload,
      };
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Params
    builder
      .addCase(fetchNotificationPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotificationPreferences.fulfilled, (state, action) => {
        state.loading = false;
        state.notificationPreferences = action.payload;
      })
      .addCase(fetchNotificationPreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update Params
    builder
      .addCase(updateNotificationPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateNotificationPreferences.fulfilled, (state, action) => {
        state.loading = false;
        state.notificationPreferences = action.payload;
      })
      .addCase(updateNotificationPreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Actions
export const {
  updateFilter,
  updateFilters,
  resetFilters,
  updateExchanges,
  toggleExchange,
  addWatchlistedSymbol,
  removeWatchlistedSymbol,
  setTheme,
  toggleSound,
  updateDashboardSettings,
  clearError,
} = preferencesSlice.actions;

// Selectors
export const selectFilters = (state: { preferences: PreferencesState }) =>
  state.preferences.filters;
export const selectExchanges = (state: { preferences: PreferencesState }) =>
  state.preferences.filters.exchanges;
export const selectWatchlistedSymbols = (state: {
  preferences: PreferencesState;
}) => state.preferences.filters.watchlistedSymbols;
export const selectTheme = (state: { preferences: PreferencesState }) =>
  state.preferences.theme;
export const selectSoundEnabled = (state: { preferences: PreferencesState }) =>
  state.preferences.soundEnabled;
export const selectDashboardSettings = (state: {
  preferences: PreferencesState;
}) => state.preferences.dashboardSettings;
export const selectNotificationPreferences = (state: {
  preferences: PreferencesState;
}) => state.preferences.notificationPreferences;
export const selectPreferencesLoading = (state: {
  preferences: PreferencesState;
}) => state.preferences.loading;
export const selectPreferencesError = (state: {
  preferences: PreferencesState;
}) => state.preferences.error;

export default preferencesSlice.reducer;
